import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { usePlatform } from '@stans-world/sdk/react';
import { useAuth } from '../auth/AuthContext';
import { NotFoundContent } from '../components/NotFoundPage';
import { gameComponents } from './gameRegistry';

function GamePage() {
  const { gameSlug } = useParams();
  const { authenticated, openAuth } = useAuth();
  const { gameApiUrl, getLeaderboard, primaryGame } = usePlatform();
  const [bestDistance, setBestDistance] = useState(0);
  // Daily PB is the live-update floor: a player breaking their daily but not
  // their all-time should still see the daily leaderboard climb in real time.
  const [dailyBestDistance, setDailyBestDistance] = useState(0);

  // Server-issued nonce for the current attempt (anti-cheat). Held in a ref
  // so the async `submitRunRecord` callback always sees the latest value
  // without re-binding on every change.
  const runIdRef = useRef(null);

  const onSignIn = useCallback(() => openAuth('signin'), [openAuth]);

  useEffect(() => {
    if (!gameSlug) {
      setBestDistance(0);
      setDailyBestDistance(0);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(gameApiUrl(gameSlug, '/state'), {
          method: 'GET',
          credentials: 'include',
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) {
          setBestDistance(Number(data?.state?.bestDistance) || 0);
          // Server's normalizeState already zeroes dailyBestDistance when
          // dailyResetDate is stale, so we can trust the value as-is.
          setDailyBestDistance(Number(data?.state?.dailyBestDistance) || 0);
        }
      } catch {
        /* swallow — best-distance display is optional */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authenticated, gameApiUrl, gameSlug]);

  /**
   * Begin a server-anchored run. POSTs /run/start, stashes the returned
   * runId in a ref, and returns true on success. The game calls this once
   * the player provides input; subsequent /run POSTs include the runId so
   * the server can reject inflated/instant scores (anti-cheat).
   *
   * Without a session the server returns 401 — local play continues with no
   * runId, so scores are not persisted.
   */
  const onRunStart = useCallback(async () => {
    if (!gameSlug) return false;
    runIdRef.current = null;
    try {
      const res = await fetch(gameApiUrl(gameSlug, '/run/start'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) return false;
      const data = await res.json();
      const id = typeof data?.runId === 'string' ? data.runId : null;
      runIdRef.current = id;
      return Boolean(id);
    } catch {
      return false;
    }
  }, [gameApiUrl, gameSlug]);

  /**
   * Submit a /run record.
   *
   * `final: false` — mid-run personal-best heartbeat. The server skips PG and
   *   only writes Redis (ZADD GT). Response is `{ ok: true }`. We refresh the
   *   leaderboard live but leave local PB state alone — the player hasn't
   *   officially set a record yet, and the next confirmed value will arrive
   *   on the `final: true` POST.
   *
   * `final: true` (default) — the run ended (crash). Server reads + writes
   *   PG, mirrors to Redis, returns `{ state }`. We sync local PB state and
   *   refresh both the leaderboard and the user's primary-game state row.
   *
   * Both paths require a server-issued runId from `/run/start`; if the host
   * never got one (network failure, anonymous user), nothing is submitted.
   */
  const submitRunRecord = useCallback(
    async (distance, { final }) => {
      if (!gameSlug) return;
      const runId = runIdRef.current;
      if (!runId) return;
      const n = Math.round(Number(distance));
      if (!Number.isFinite(n) || n <= 0) return;
      try {
        const res = await fetch(gameApiUrl(gameSlug, '/run'), {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ distance: n, final, runId }),
        });
        if (!res.ok) {
          // Server invalidates the runId on `final: true` and on cheat-detect.
          // Drop the local copy either way so we don't keep retrying with a
          // dead nonce — the next /run/start will mint a fresh one.
          if (final) runIdRef.current = null;
          return;
        }
        // Refresh the leaderboard for both heartbeats and final crashes —
        // that's the whole point of the heartbeat path.
        try {
          getLeaderboard?.(gameSlug)?.refresh?.();
        } catch {
          /* refresh is best-effort */
        }
        if (!final) return;
        runIdRef.current = null;
        const data = await res.json();
        const nextBest = Number(data?.state?.bestDistance) || 0;
        const nextDaily = Number(data?.state?.dailyBestDistance) || 0;
        setBestDistance((cur) => Math.max(cur, nextBest));
        setDailyBestDistance((cur) => Math.max(cur, nextDaily));
        if (gameSlug === primaryGame?.id) {
          try {
            primaryGame?.refresh?.();
          } catch {
            /* refresh is best-effort */
          }
        }
      } catch {
        /* network failures shouldn't break the game */
      }
    },
    [gameApiUrl, gameSlug, getLeaderboard, primaryGame],
  );

  const onRunEnd = useCallback(
    (distance) => submitRunRecord(distance, { final: true }),
    [submitRunRecord],
  );

  const onLiveBest = useCallback(
    (distance) => submitRunRecord(distance, { final: false }),
    [submitRunRecord],
  );

  if (!gameSlug) {
    return <Navigate to="/hub" replace />;
  }
  const Comp = gameComponents[gameSlug];
  if (!Comp) {
    return <NotFoundContent variant="game" />;
  }
  return (
    <Comp
      authenticated={authenticated}
      onSignIn={onSignIn}
      onRunStart={onRunStart}
      onRunEnd={onRunEnd}
      onLiveBest={onLiveBest}
      bestDistance={bestDistance}
      dailyBestDistance={dailyBestDistance}
    />
  );
}

export default GamePage;
